// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import clsx from "clsx";
import moment from "moment";
import { ColumnsType } from "antd/es/table";
import { DatePicker, Radio, Tooltip } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { ReactSVG } from "react-svg";

// Local Imports
import CustomTable from "../../../../components/custom-table/custom-table";
import CustomSearch from "../../../../components/custom-search/custom-search";
import PortfoliosService from "../../portfolios.service";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import DeleteModal from "src/components/delete-modal/delete-modal";
import CustomModal from "src/components/custom-modal/custom-modal";
import portfolioAgreementService from "src/views/portfolio-agreement/portfolio-agreement.service";
import Label from "src/components/label/label.component";

const Completed: React.FC = (): ReactElement => {
  let searchTimer = null;
  const user = useSelector((state: any) => state.user);
  const portfoliosService = new PortfoliosService();
  const loadingState = useSelector((state: any) => state?.loading);
  const navigate = useNavigate();
  const [completedRequestsList, setCompletedRequestsList] = useState<any[]>([]);
  const [responseDate, setResponseDate] = useState<any>({});
  const [searchWord, setSearchWord] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [filters, setFilters] = useState({});
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [openGenerateKycModal, setOpenGenerateKycModal] = useState(false);
  const [viewOnly, setViewOnly] = useState(true);
  const [asOfDate, setAsOfDate] = useState(null);

  useEffect(() => {
    fetchCompletedListData();
  }, []);

  const columns: ColumnsType<any> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      render: (_, __, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "Client account",
      dataIndex: "accountCode",
      key: "accountCode",
      sorter: true,
      fixed: "left",
      render: (_, record) => (
        <Link
          to={`/home/business-account/${record?.accountId}/${record?.account?.activeKycId}/details`}
        >
          {record?.account?.accountCode}
        </Link>
      ),
    },
    {
      title: "Client name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      render: (_, record) => record?.account?.activeKyc?.nameEn,
    },
    {
      title: "Wealth manager",
      dataIndex: "wealthManager",
      key: "wealthManager",
      render: (_, record) => record?.account?.wealthManager?.ae1 || "-",
    },
    {
      title: "Portfolio Type",
      dataIndex: "portfolioType",
      key: "portfolioType",
      render: (_, record) => <span>{record?.portfolioType?.nameEn}</span>,
    },
    {
      title: "Portfolio#",
      dataIndex: "accountCode",
      key: "accountCode",
      sorter: (a, b) => a.accountCode - b.accountCode,
    },
    {
      title: "Request status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      filters: [
        {
          text: "Approved",
          value: "Approved",
        },
        {
          text: "Opened",
          value: "Opened",
        },
        {
          text: "Rejected",
          value: "Rejected",
        },
      ],
      onFilter: (value, record) => record.status === value,
      render: (text) => (
        <span
          className={clsx(
            "rounded bg-gray-600 text-xs font-medium w-fit py-2 px-3",
            text !== "Rejected"
              ? "bg-gray-600 text-green-500"
              : "bg-pink-200 text-pink-300"
          )}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Origin",
      dataIndex: "origin",
      key: "origin",
      render: (text) =>
        text !== "APP" ? (
          <ReactSVG src={"/images/monitor.svg"} />
        ) : (
          <ReactSVG src={"/images/mobile.svg"} />
        ),
    },
    {
      title: "Action by",
      dataIndex: "actionBy",
      key: "actionBy",
      render: (_, record) => <span>{record?.actionBy?.name || "-"}</span>,
    },
    {
      title: "Action Date",
      dataIndex: "actionDate",
      key: "actionDate",
      sorter: true,
      render: (text) =>
        text ? (
          <span className=" text-gray-500">
            {moment(text).format("MMM DD YYYY hh:mm A")}
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <Tooltip title="View">
            <span
              onClick={() => {
                navigate(`/portfolio/take-action/${record.id}`);
              }}
              className="cursor-pointer"
            >
              <ReactSVG src={"images/view.svg"} />
            </span>
          </Tooltip>
          {user?.permissions?.includes("portfolio_agreement_regenerate") && <Tooltip title="Regenerate Agreement">
            <span
              onClick={() => {
                setSelectedRecord(record);
                setOpenGenerateKycModal(true);
              }}
              className="cursor-pointer"
            >
              <ReactSVG src={"images/resend.svg"} />
            </span>
          </Tooltip>}
        </div>
      ),
    },
  ];

  const fetchCompletedListData = async (page = 1): Promise<void> => {
    const data = await portfoliosService.getPortfoliosListData(
      ["Rejected", "Opened"],
      false,
      page,
      searchWord,
      sortField,
      sortOrder,
      filters,
      ["App", "Portal"],
    );

    if (data !== undefined) {
      setResponseDate(data.data);
      setCompletedRequestsList(data.data?.portfolios);
    }
  };

  const handleRegeneratePdf = async (portfolioId: number): Promise<void> => {
    try {
      const response = await portfoliosService.regeneratePortfolioPdf(portfolioId, viewOnly, asOfDate);

      if (response) {

        // Check if response itself is the base64 string or if it's nested in response.data
        const pdfData = typeof response === 'string' ? response : (response?.data || response?.pdf || response?.file);
        downloadPdf(pdfData, "portfolio.pdf");
        setOpenGenerateKycModal(false);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to regenerate PDF");
    } finally {
      setOpenGenerateKycModal(false);
    }
  };

  const downloadPdf = (base64String, fileName) => {
    // Clean the base64 string - remove data URL prefix if present and whitespace
    let cleanBase64 = base64String;
    if (typeof cleanBase64 === 'string') {
      // Remove data URL prefix if present (e.g., "data:application/pdf;base64,")
      if (cleanBase64.includes(',')) {
        cleanBase64 = cleanBase64.split(',')[1];
      }
      // Remove any whitespace/newlines
      cleanBase64 = cleanBase64.replace(/\s/g, '');
    }

    // Convert Base64 to Blob
    const byteCharacters = atob(cleanBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });

    // Create a link element
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName || "document.pdf"; // Default file name

    // Append the link to the body (required for Firefox)
    document.body.appendChild(link);

    // Trigger the download
    link.click();

    // Remove the link from the document
    document.body.removeChild(link);
  };

  useEffect(() => {
    fetchCompletedListData();
  }, [searchWord, sortField, sortOrder, filters]);

  const handleTableChange = (pagination, filters, sorter) => {
    setFilters(JSON.stringify(filters?.status));
    setSortField(sorter.field);
    setSortOrder(sorter.order);
  };

  const onSearch = (e) => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      setSearchWord(e.target.value);
    }, 1000);
  };

  return (
    <div className=" mt-5 border-t-[1px] border-gray-400 rounded-lg">
      <div className="flex items-center justify-between rounded-lg bg-white py-[10px] px-5">
        <p className=" text-black-300 text-base font-bold">
          {!loadingState ? completedRequestsList?.length : ""} Completed
          requests
        </p>
        <div className="flex items-center">
          <CustomSearch onChange={(event) => onSearch(event)} />
        </div>
      </div>
      {completedRequestsList?.length ? (
        <CustomTable
          columns={columns}
          data={completedRequestsList}
          showPagination={true}
          pageSize={responseDate?.pageSize}
          totalCount={responseDate?.totalCount}
          page={responseDate?.currentPage}
          handleTableChange={handleTableChange}
          onPageChange={(page) => {
            fetchCompletedListData(page);
          }}
        />
      ) : !loadingState ? (
        <div className="flex flex-col gap-3 w-full items-center justify-center mt-9">
          <img
            className="w-[40px] h-[40px]"
            src="/images/no-items.svg"
            alt="no data kamco"
          />
          <p className="text-lg text-gray-500 font-medium">
            There are no items in this list
          </p>
        </div>
      ) : (
        ""
      )}

      <CustomModal
        isVisible={openGenerateKycModal}
        centered
        showClose={false}
        onClose={() => {
          setOpenGenerateKycModal(false);
        }}
        className="generate-modal"
      >
        <div className="w-full h-full py-5 bg-white rounded-xl shadow-[0px_0px_4px_0px_rgba(0,0,0,0.16)] flex-col justify-center items-end gap-5 inline-flex">
          <div className="flex-col justify-start items-end gap-10 flex">
            <div className="flex-col justify-start items-start flex">
              <div className="self-stretch h-7 flex-col justify-start items-start gap-5 flex">
                <div className="self-stretch h-7 px-5 flex-col justify-start items-start gap-4 flex">
                  <div className="self-stretch justify-start items-start gap-4 inline-flex">
                    <div className="grow shrink basis-0 text-black text-2xl font-bold  tracking-tight">
                      Generate KYC PDF
                    </div>
                    <div className="w-6 h-6 justify-center items-center flex">
                      <div className="w-6 h-6 relative"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="self-stretch flex-col justify-start items-start flex">
                <div className="px-5 justify-start items-start gap-5 inline-flex">
                  <div className="self-stretch py-5 flex-col justify-start items-start gap-5 inline-flex">
                    <div className="self-stretch text-[#535353] text-sm font-medium  leading-7">
                      You're trying to generate KYC PDF, please select on of the
                      below options
                    </div>
                    <Radio.Group
                      defaultValue={0}
                      className="flex flex-col gap-3"
                      onChange={(e) => {
                        if (e.target.value === 1) {
                          setViewOnly(false);
                        } else {
                          setViewOnly(true);
                        }
                        if (e.target.value === 0) {
                          setViewOnly(true);
                        }
                      }}
                    >
                      <Radio value={0}>View only</Radio>
                      <Radio value={1}>Upload to PROSPERO only</Radio>

                    </Radio.Group>

                    <div className="flex flex-col gap-1 w-full">
                      <Label text="As of Date (Optional)" />
                      <DatePicker
                        placeholder="Select date"
                        showToday={false}
                        onChange={(date) => setAsOfDate(date?.toISOString())}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch px-5 justify-end items-start gap-5 inline-flex">
            <div
              onClick={() => setOpenGenerateKycModal(false)}
              className="cursor-pointer grow shrink basis-0 h-12 p-4 bg-white rounded-lg border border-[#002f6c] justify-center items-center gap-2.5 flex"
            >
              <div className="text-center text-[#002f6c] text-sm font-bold  leading-none">
                Cancel
              </div>
            </div>
            <div
              onClick={async () => handleRegeneratePdf(selectedRecord?.id)}
              className="cursor-pointer grow shrink basis-0 h-12 p-3 bg-[#002f6c] rounded-lg justify-center items-center gap-2 flex"
            >
              <div className="text-center text-white text-sm font-bold  leading-none">
                Submit
              </div>
            </div>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default Completed;
